import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Clock,
  FileText,
  CreditCard,
  CalendarClock,
  DollarSign,
  AlertCircle,
  TrendingUp,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Loader2,
  ClipboardCheck,
  Shield,
} from "lucide-react";
import {
  timeEntriesApi,
  invoicesApi,
  paymentsApi,
  stripeConnectApi,
} from "../../services/api";
import InvoicesTab from "./InvoicesTab";
import PaymentsTab from "./PaymentsTab";
import PaymentPlansTab from "./PaymentPlansTab";
import PreBillReviewTab from "./PreBillReviewTab";
import BillingRulesTab from "./BillingRulesTab";

type TabKey = "invoices" | "payments" | "plans" | "pre_bill" | "rules";

interface SummaryData {
  totalUnbilled: number;
  outstandingInvoices: number;
  collectedThisMonth: number;
}

const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "invoices", label: "Invoices", icon: <FileText className="h-4 w-4" /> },
  { key: "pre_bill", label: "Pre-Bill Review", icon: <ClipboardCheck className="h-4 w-4" /> },
  { key: "payments", label: "Payments", icon: <CreditCard className="h-4 w-4" /> },
  { key: "plans", label: "Payment Plans", icon: <CalendarClock className="h-4 w-4" /> },
  { key: "rules", label: "Billing Rules", icon: <Shield className="h-4 w-4" /> },
];

const formatCurrency = (cents: number, currency = "CAD") => {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency,
  }).format(cents / 100);
};

const PRIMARY = "var(--color-primary)";
const HOVER = "var(--color-primary-hover)";

const BillingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>("invoices");
  const [summary, setSummary] = useState<SummaryData>({
    totalUnbilled: 0,
    outstandingInvoices: 0,
    collectedThisMonth: 0,
  });
  const [loadingSummary, setLoadingSummary] = useState(true);

  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState<"loading" | "not_connected" | "pending" | "connected">("loading");
  const [connectingStripe, setConnectingStripe] = useState(false);

  useEffect(() => {
    fetchSummary();
    fetchStripeStatus();
  }, []);

  const fetchStripeStatus = async () => {
    try {
      const res = await stripeConnectApi.getStatus();
      const data = res.data?.data || res.data;
      if (data?.chargesEnabled && data?.payoutsEnabled) {
        setStripeStatus("connected");
      } else if (data?.accountId) {
        setStripeStatus("pending");
      } else {
        setStripeStatus("not_connected");
      }
    } catch {
      setStripeStatus("not_connected");
    }
  };

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const res = await stripeConnectApi.createAccountLink();
      const url = res.data?.data?.url || res.data?.url;
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      console.error("Failed to create Stripe account link:", err);
    } finally {
      setConnectingStripe(false);
    }
  };

  const handleStripeDashboard = async () => {
    try {
      const res = await stripeConnectApi.getDashboardLink();
      const url = res.data?.data?.url || res.data?.url;
      if (url) {
        window.open(url, "_blank");
      }
    } catch (err) {
      console.error("Failed to get Stripe dashboard link:", err);
    }
  };

  const fetchSummary = async () => {
    setLoadingSummary(true);
    try {
      // Fetch unbilled time entries total
      const timeRes = await timeEntriesApi.list({ status: "unbilled", limit: 1 });
      const unbilledEntries = await timeEntriesApi.list({ status: "unbilled", limit: 500 });
      const totalUnbilled = (unbilledEntries.data?.data || []).reduce(
        (sum: number, te: any) => sum + (te.amountCents || 0),
        0
      );

      // Fetch outstanding invoices
      const issuedRes = await invoicesApi.list({ status: "issued", limit: 500 });
      const partialRes = await invoicesApi.list({ status: "partial", limit: 500 });
      const outstandingInvoices = [
        ...(issuedRes.data?.data || []),
        ...(partialRes.data?.data || []),
      ].reduce((sum: number, inv: any) => sum + (inv.balanceDueCents || 0), 0);

      // Fetch payments this month
      const now = new Date();
      const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      const paymentsRes = await paymentsApi.list({
        status: "succeeded",
        dateFrom: firstOfMonth,
        limit: 500,
      });
      const collectedThisMonth = (paymentsRes.data?.data || []).reduce(
        (sum: number, p: any) => sum + (p.amountCents || 0),
        0
      );

      setSummary({ totalUnbilled, outstandingInvoices, collectedThisMonth });
    } catch (err) {
      console.error("Failed to fetch billing summary:", err);
    } finally {
      setLoadingSummary(false);
    }
  };

  const summaryCards = [
    {
      label: "Unbilled Time",
      value: formatCurrency(summary.totalUnbilled),
      icon: <Clock className="h-5 w-5" />,
      color: "text-amber-600",
      bg: "bg-amber-50",
    },
    {
      label: "Outstanding Invoices",
      value: formatCurrency(summary.outstandingInvoices),
      icon: <AlertCircle className="h-5 w-5" />,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      label: "Collected This Month",
      value: formatCurrency(summary.collectedThisMonth),
      icon: <TrendingUp className="h-5 w-5" />,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Billing</h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage invoices, payments, and payment plans
        </p>
      </div>

      {/* Stripe Connect Banner */}
      {stripeStatus !== "loading" && stripeStatus !== "connected" && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 rounded-xl border border-brand-light bg-gradient-to-r from-brand-light via-white to-brand-light overflow-hidden"
        >
          <div className="flex items-center gap-6 px-6 py-5">
            {/* Stripe icon area */}
            <div
              className="hidden sm:flex items-center justify-center h-14 w-14 rounded-2xl flex-shrink-0"
              style={{ backgroundColor: PRIMARY }}
            >
              <CreditCard className="h-7 w-7 text-white" />
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-slate-800">
                {stripeStatus === "pending"
                  ? "Finish setting up Stripe"
                  : "Connect Stripe to receive payments"}
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                {stripeStatus === "pending"
                  ? "Your Stripe account setup is incomplete. Continue onboarding to start accepting payments from clients."
                  : "Accept credit card and bank payments directly from your invoices. Funds are deposited straight into your bank account."}
              </p>
            </div>

            {/* CTA */}
            <button
              onClick={handleConnectStripe}
              disabled={connectingStripe}
              className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl transition-colors disabled:opacity-60"
              style={{ backgroundColor: PRIMARY }}
              onMouseEnter={(e) => {
                if (!connectingStripe) e.currentTarget.style.backgroundColor = HOVER;
              }}
              onMouseLeave={(e) => {
                if (!connectingStripe) e.currentTarget.style.backgroundColor = PRIMARY;
              }}
            >
              {connectingStripe ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  {stripeStatus === "pending" ? "Continue Setup" : "Connect Stripe"}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </motion.div>
      )}

      {/* Connected badge (subtle) */}
      {stripeStatus === "connected" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
            <CheckCircle2 className="h-4 w-4" />
            Stripe is connected  -  you can accept payments
          </div>
          <button
            onClick={handleStripeDashboard}
            className="text-sm font-medium text-emerald-600 hover:text-emerald-800 flex items-center gap-1 transition-colors"
          >
            Stripe Dashboard
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {summaryCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{card.label}</p>
                {loadingSummary ? (
                  <div className="h-8 w-24 bg-slate-100 rounded animate-pulse mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-slate-800 mt-1">{card.value}</p>
                )}
              </div>
              <div className={`p-3 rounded-xl ${card.bg}`}>
                <span className={card.color}>{card.icon}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="relative flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap"
              style={{
                color: activeTab === tab.key ? "var(--color-primary)" : "#64748b",
              }}
            >
              {tab.icon}
              {tab.label}
              {activeTab === tab.key && (
                <motion.div
                  layoutId="billing-tab-indicator"
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeTab === "invoices" && <InvoicesTab onDataChange={fetchSummary} />}
        {activeTab === "pre_bill" && <PreBillReviewTab onDataChange={fetchSummary} />}
        {activeTab === "payments" && <PaymentsTab onDataChange={fetchSummary} />}
        {activeTab === "plans" && <PaymentPlansTab />}
        {activeTab === "rules" && <BillingRulesTab />}
      </motion.div>
    </div>
  );
};

export default BillingPage;
